import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { create } from "zustand";
import {
  allCommands,
  displayKeys,
  effectiveKeys,
  setShortcutOverride,
  subscribeCommands,
  type AppCommand,
} from "@/commands/registry";
import { useSettings } from "@/store/settings";
import { useAuth } from "@/data/auth";
import { sharedAccent } from "@/data/sharedFolders";
import { IconButton } from "@/ui/IconButton";
import { Button } from "@/ui/Button";
import { LogoMark, Wordmark } from "@/ui/Logo";
import { AdminTab } from "./AdminTab";
import { DevelopersTab } from "@/developers/DevelopersTab";
import { fetchQuota, fmtBytes, type QuotaInfo } from "./quota";
import s from "./SettingsDialog.module.css";

type Tab = "appearance" | "shortcuts" | "account" | "developers" | "admin" | "about";

interface SettingsDialogStore {
  open: boolean;
  /** Tab to show when opening; consumed on open. */
  initialTab: Tab | null;
  setOpen: (open: boolean, tab?: Tab) => void;
}
export const useSettingsDialog = create<SettingsDialogStore>((set) => ({
  open: false,
  initialTab: null,
  setOpen: (open, tab) => set({ open, initialTab: tab ?? null }),
}));

export function SettingsDialog() {
  const { open, setOpen, initialTab } = useSettingsDialog();
  const [tab, setTab] = useState<Tab>("appearance");
  useEffect(() => {
    if (open && initialTab) setTab(initialTab);
  }, [open, initialTab]);
  const user = useAuth((a) => a.user);

  const tabs: [Tab, string][] = [
    ["appearance", "Appearance"],
    ["shortcuts", "Shortcuts"],
    ["account", "Account"],
    ["developers", "Developers"],
    ...(user?.isAdmin ? ([["admin", "Admin"]] as [Tab, string][]) : []),
    ["about", "About"],
  ];

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={[s.dialog, tab === "admin" || tab === "developers" ? s.dialogWide : ""].join(" ")}>
          <nav className={s.nav}>
            {tabs.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={[s.navItem, tab === id ? s.navActive : ""].join(" ")}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className={s.content}>
            {tab === "appearance" ? <AppearanceTab /> : null}
            {tab === "shortcuts" ? <ShortcutsTab /> : null}
            {tab === "account" ? <AccountTab /> : null}
            {tab === "developers" ? <DevelopersTab /> : null}
            {tab === "admin" && user?.isAdmin ? (
              <>
                <h2 className={s.sectionTitle}>Admin — all accounts</h2>
                <AdminTab selfId={user.id} />
              </>
            ) : null}
            {tab === "about" ? <AboutTab /> : null}
            <Dialog.Close asChild>
              <IconButton label="Close" className={s.close}>
                <X size={14} />
              </IconButton>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AppearanceTab() {
  const settings = useSettings();
  return (
    <>
      <h2 className={s.sectionTitle}>Appearance</h2>
      <div className={s.row}>
        <span className={s.rowLabel}>
          Editor font size
          <span className={s.rowHint}>{settings.editorFontSize}px</span>
        </span>
        <input
          type="range"
          className={s.range}
          min={12}
          max={22}
          value={settings.editorFontSize}
          onChange={(e) => settings.setEditorFontSize(Number(e.target.value))}
        />
      </div>
      <div className={s.row}>
        <span className={s.rowLabel}>
          Editor width
          <span className={s.rowHint}>
            {settings.editorWidth === "full"
              ? "Notes take the space available, with tasteful margins"
              : settings.editorWidth === "wide"
                ? "A wide fixed column"
                : "A classic readable column"}
          </span>
        </span>
        <div className={s.segment}>
          {(["narrow", "wide", "full"] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={[s.segmentBtn, settings.editorWidth === w ? s.segmentActive : ""].join(" ")}
              onClick={() => settings.setEditorWidth(w)}
            >
              {w[0]!.toUpperCase() + w.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className={s.row}>
        <span className={s.rowLabel}>
          Task completion celebrations
          <span className={s.rowHint}>Confetti and check animations when you complete tasks</span>
        </span>
        <div className={s.segment}>
          {([true, false] as const).map((v) => (
            <button
              key={String(v)}
              type="button"
              className={[s.segmentBtn, settings.celebrationsEnabled === v ? s.segmentActive : ""].join(" ")}
              onClick={() => settings.setCelebrationsEnabled(v)}
            >
              {v ? "On" : "Off"}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function ShortcutsTab() {
  const [, force] = useState(0);
  const [recording, setRecording] = useState<string | null>(null);

  useEffect(() => subscribeCommands(() => force((n) => n + 1)), []);

  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") {
        setRecording(null);
        return;
      }
      if (["Control", "Meta", "Shift", "Alt"].includes(e.key)) return;
      const parts: string[] = [];
      if (e.ctrlKey || e.metaKey) parts.push("$mod");
      if (e.shiftKey) parts.push("Shift");
      if (e.altKey) parts.push("Alt");
      if (parts.length === 0) return; // require a modifier
      parts.push(e.key.length === 1 ? e.key.toLowerCase() : e.key);
      setShortcutOverride(recording, parts.join("+"));
      setRecording(null);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [recording]);

  const bySection = new Map<string, AppCommand[]>();
  for (const cmd of allCommands().filter((c) => !c.hidden)) {
    const list = bySection.get(cmd.section) ?? [];
    list.push(cmd);
    bySection.set(cmd.section, list);
  }

  return (
    <>
      <h2 className={s.sectionTitle}>Keyboard shortcuts</h2>
      {[...bySection.entries()].map(([section, cmds]) => (
        <div key={section}>
          {cmds.map((cmd) => {
            const keys = effectiveKeys(cmd);
            const isRecording = recording === cmd.id;
            return (
              <div key={cmd.id} className={s.row}>
                <span className={s.rowLabel}>
                  {cmd.title}
                  <span className={s.rowHint}>{section}</span>
                </span>
                <button
                  type="button"
                  className={[s.kbdBtn, isRecording ? s.recording : ""].join(" ")}
                  onClick={() => setRecording(isRecording ? null : cmd.id)}
                >
                  {isRecording ? "Press keys…" : keys ? displayKeys(keys) : "Not set"}
                </button>
                {keys !== cmd.defaultKeys ? (
                  <Button size="sm" variant="subtle" onClick={() => setShortcutOverride(cmd.id, null)}>
                    Reset
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

interface DeviceSession {
  id: string;
  userAgent: string | null;
  createdAt: number;
  lastSeenAt: number;
  current: boolean;
}

function describeUa(ua: string | null): string {
  if (!ua) return "Unknown device";
  // the native iOS app: "HydraNotes/1.0 (iPhone; iOS 26.0)"
  const native = /^HydraNotes\/[^ ]+ \((iPhone|iPad)/.exec(ua);
  if (native) return `Hydra Notes on ${native[1]}`;
  const browser = /firefox/i.test(ua) ? "Firefox" : /edg\//i.test(ua) ? "Edge" : /chrome/i.test(ua) ? "Chrome" : /safari/i.test(ua) ? "Safari" : "Browser";
  const os = /windows/i.test(ua) ? "Windows" : /mac os/i.test(ua) ? "macOS" : /android/i.test(ua) ? "Android" : /iphone|ipad/i.test(ua) ? "iOS" : /linux/i.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}

function AccountTab() {
  const { status, user, signOut } = useAuth();
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [devices, setDevices] = useState<DeviceSession[]>([]);

  const loadDevices = () =>
    fetch("/api/auth/sessions")
      .then((r) => (r.ok ? r.json() : { sessions: [] }))
      .then((d: { sessions: DeviceSession[] }) => setDevices(d.sessions ?? []))
      .catch(() => {});

  useEffect(() => {
    if (status === "signedIn") {
      void fetchQuota().then(setQuota);
      void loadDevices();
    } else {
      setQuota(null);
      setDevices([]);
    }
  }, [status]);

  const revoke = async (id: string) => {
    await fetch(`/api/auth/sessions/${id}`, { method: "DELETE" });
    await loadDevices();
  };

  return (
    <>
      <h2 className={s.sectionTitle}>Account</h2>
      {status === "signedIn" && user ? (
        <>
          <div className={s.row}>
            <span className={s.rowLabel}>
              {user.email} {user.isPremium ? <span className={s.premiumBadge}>Premium</span> : null}
              <span className={s.rowHint}>Cloud sync active on this device</span>
            </span>
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
          {devices.length > 0 ? (
            <>
              <h2 className={s.sectionTitle} style={{ marginTop: 24 }}>
                Devices signed in
              </h2>
              {devices.map((d) => (
                <div key={d.id} className={s.row}>
                  <span className={s.rowLabel}>
                    {describeUa(d.userAgent)}
                    {d.current ? <span className={s.premiumBadge}>This device</span> : null}
                    <span className={s.rowHint}>
                      Last active {new Date(d.lastSeenAt).toLocaleString()} · signed in{" "}
                      {new Date(d.createdAt).toLocaleDateString()}
                    </span>
                  </span>
                  {!d.current ? (
                    <Button variant="ghost" size="sm" onClick={() => void revoke(d.id)}>
                      Sign out device
                    </Button>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
          {quota ? (
            <div className={s.row}>
              <span className={s.rowLabel}>
                Storage: {fmtBytes(quota.usedBytes)} of {fmtBytes(quota.quotaBytes)}
                <span className={s.rowHint}>
                  {fmtBytes(quota.noteBytes)} notes · {fmtBytes(quota.attachmentBytes)} attachments
                  {quota.sharedFolders?.length
                    ? ` · ${fmtBytes(quota.sharedFolders.reduce((a, f) => a + f.sizeBytes, 0))} shared`
                    : ""}
                  {quota.overQuota ? ". Storage full, new attachments are blocked" : ""}
                </span>
                <span className={s.quotaBar}>
                  <span
                    className={[
                      s.quotaFill,
                      quota.usedBytes / quota.quotaBytes >= 1
                        ? s.quotaOver
                        : quota.usedBytes / quota.quotaBytes >= 0.85
                          ? s.quotaWarn
                          : "",
                    ].join(" ")}
                    style={{ width: `${Math.min(100, (quota.usedBytes / quota.quotaBytes) * 100)}%` }}
                  />
                </span>
                {quota.sharedFolders?.length ? (
                  <span className={s.rowHint}>
                    Shared folders count fully against every member.
                    {quota.sharedFolders.map((f) => (
                      <span key={f.id} style={{ display: "block" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            marginRight: 6,
                            background: sharedAccent(f.colorKey),
                          }}
                        />
                        {f.title}: {fmtBytes(f.sizeBytes)} ({f.role})
                        {f.locked ? " · locked, over your quota" : ""}
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <div className={s.row}>
          <span className={s.rowLabel}>
            Not signed in
            <span className={s.rowHint}>Notes are stored in this browser only</span>
          </span>
        </div>
      )}
    </>
  );
}

function AboutTab() {
  return (
    <>
      <h2 className={s.sectionTitle}>About</h2>
      <div className={s.about}>
        <p style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LogoMark size={28} /> <Wordmark size={16} />
        </p>
        <p>
          Version {__APP_VERSION__}. Fast, dark, offline-first notes with bulletproof sync.
          <br />
          Built by Team Hydra.
        </p>
      </div>
    </>
  );
}
